import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {SUPPLIER_360_SETTINGS_URL, supplier360SettingsValuesUrl} from "~/pages/supplier-360/services/supplier360Urls";
import type {
    Supplier360Settings,
    SupplierLogoPlace,
    SupplierLogoProvider,
} from "~/pages/supplier-360/types/Supplier360Types";

/** `GET supplier_360/settings/` — read by every user: the detail page drops hidden tabs, logos follow the grid. */
export function useSupplier360SettingsQuery() {
    return useCustomQuery({
        queryKey: [SUPPLIER_360_SETTINGS_URL] as const,
        queryFn: async () => (await queryHttp.get<Supplier360Settings>(SUPPLIER_360_SETTINGS_URL)).data,
        staleTime: 5 * 60 * 1000,
    });
}

export type Supplier360SettingsChanges = {
    hidden_tabs?: string[];
    /** Any part of the grid; the server keeps the rest. */
    source_logos?: Partial<Record<SupplierLogoPlace, Partial<Record<SupplierLogoProvider, boolean>>>>;
};

/** `PATCH supplier_360/settings/values/` — Mithra staff only; the server refuses everyone else. */
export function useUpdateSupplier360SettingsMutation() {
    const queryClient = useQueryClient();

    return useMutation<Supplier360Settings, DefaultError, Supplier360SettingsChanges>({
        mutationFn: async changes => (
            await queryHttp.patch<Supplier360Settings>(supplier360SettingsValuesUrl(), changes)
        ).data,
        onSuccess: settings => {
            queryClient.setQueryData([SUPPLIER_360_SETTINGS_URL], settings);
            notificationStore.show('Settings saved.', {
                severity: 'success',
                id: 'supplier-360-settings-success',
                autoHideDuration: 3000,
            });
        },
        onError: () => {
            notificationStore.show('Could not save the settings. Please try again.', {
                severity: 'error',
                id: 'supplier-360-settings-error',
                autoHideDuration: 4000,
            });
        },
    });
}
